import { supportsLambdaAuthorizer } from '@/services/amplify/authMode'
import { PROJECT_IMAGE_MEDIA_WRITES_ENABLED } from './projectImageMediaGate'

import {
  type MediaAttachment,
  type MediaCreation,
  mediaReference,
  type MediaScope,
  type ScopedMediaObject,
  type ScopedUploadIntent,
  uploadScopedMedia,
} from './scopedMedia'

export type ProjectImageSelection = {
  isNew?: boolean
  scope?: MediaScope
  publication?: MediaAttachment
  creation?: Extract<MediaCreation, { model: 'Project' }>
  projectID: string | undefined
  // Local invalidation snapshots only. Data derives the actor from its authorizer.
  userID: string | undefined
  organizationID: string | undefined
  picture: string | null | undefined
  file: File | undefined
  canEdit: boolean
  isOpen: boolean
}

export type ProjectImageIntent = ScopedUploadIntent & {
  selection: ProjectImageSelection
}

export type ProjectImageUpload = {
  intent: ProjectImageIntent
  isCurrent: () => boolean
}

export function isCurrentProjectImageSelection(
  intent: ProjectImageIntent,
  current: ProjectImageSelection,
): boolean {
  const source = intent.selection
  return Boolean(
    current.isNew === source.isNew &&
    current.scope?.kind === source.scope?.kind &&
    current.scope?.id === source.scope?.id &&
    current.publication?.model === source.publication?.model &&
    current.publication?.id === source.publication?.id &&
    current.publication?.field === source.publication?.field &&
    current.creation?.model === source.creation?.model &&
    current.creation?.field === source.creation?.field &&
    current.canEdit &&
    current.isOpen &&
    source.projectID &&
    source.userID &&
    current.projectID === source.projectID &&
    current.userID === source.userID &&
    current.organizationID === source.organizationID &&
    current.file === source.file &&
    (current.picture === source.picture ||
      (intent.object && current.picture === mediaReference(intent.object))),
  )
}

export function assertCurrentProjectImage(upload: ProjectImageUpload): void {
  if (!upload.isCurrent()) throw new Error('Project image selection changed')
}

export async function publishNewProjectImage(
  upload: ProjectImageUpload,
): Promise<ScopedMediaObject> {
  if (!PROJECT_IMAGE_MEDIA_WRITES_ENABLED || !supportsLambdaAuthorizer()) {
    throw new Error('Project media writes are disabled')
  }
  assertCurrentProjectImage(upload)
  const { intent } = upload
  const { file, projectID } = intent.selection
  if (
    !projectID ||
    !file ||
    !file.size ||
    file.size >= 5_000_000 ||
    !['image/png', 'image/jpeg'].includes(file.type)
  ) {
    throw new Error('Invalid project image')
  }
  const scope =
    intent.selection.scope ??
    (!intent.selection.isNew
      ? { kind: 'PROJECT' as const, id: projectID }
      : undefined)
  const attachment =
    intent.selection.publication ??
    (!intent.selection.isNew
      ? { model: 'Project' as const, id: projectID, field: 'picture' }
      : undefined)
  const creation = intent.selection.creation
  if (
    !scope ||
    (!attachment && !creation) ||
    Boolean(attachment && creation) ||
    Boolean(
      creation &&
      (!intent.selection.isNew ||
        scope.kind !== 'SELF' ||
        scope.id !== intent.selection.userID),
    )
  )
    throw new Error(
      'A public project image requires an existing attachment context or creation context',
    )
  const bytes = await file.arrayBuffer()
  const signature = new Uint8Array(bytes, 0, Math.min(bytes.byteLength, 8))
  const png = [137, 80, 78, 71, 13, 10, 26, 10]
  const matchesType =
    file.type === 'image/png'
      ? png.every((byte, index) => signature[index] === byte)
      : signature[0] === 255 && signature[1] === 216 && signature[2] === 255
  if (!matchesType)
    throw new Error('Project image MIME does not match its bytes')
  const extension = file.type === 'image/png' ? 'png' : 'jpg'
  const digest = await crypto.subtle.digest('SHA-256', bytes)
  const sha256 = btoa(String.fromCharCode(...new Uint8Array(digest)))
  assertCurrentProjectImage(upload)
  const publication = await uploadScopedMedia({
    input: {
      scope,
      access: 'PUBLIC',
      transport: 'DIRECT',
      ...(attachment ? { publication: attachment } : { creation: creation! }),
      contentType: file.type,
      contentLength: bytes.byteLength,
      sha256,
      extension,
    },
    body: file,
    intent,
    assertCurrent: () => assertCurrentProjectImage(upload),
  })
  intent.object = publication
  assertCurrentProjectImage(upload)
  return publication
}
